// Radha — i18n core. No external deps; uses the platform Intl API.
//
// Rewritten in Phase 1. Previously this shipped en/es/ja packs that were wired
// to nothing and translated a 9-module nav the app no longer has. Now:
//   * the tenant's locale (063) is the default, so a UAE tenant lands in Arabic
//     without anyone toggling anything;
//   * an individual can override for their own session — a Dubai finance team
//     may well work in English — persisted per browser, never to the tenant;
//   * script direction is derived, not configured, and applied to <html> so RTL
//     mirrors the whole layout through the CSS logical properties in theme.css.
import en from "../locales/en";
import es from "../locales/es";
import de from "../locales/de";
import fr from "../locales/fr";
import nl from "../locales/nl";
import ar from "../locales/ar";
import ja from "../locales/ja";

export type Locale = "en" | "es" | "de" | "fr" | "nl" | "ar" | "ja";

const PACKS: Record<Locale, Record<string, string>> = { en, es, de, fr, nl, ar, ja };

export const AVAILABLE_LOCALES: Locale[] = ["en", "es", "de", "fr", "nl", "ar", "ja"];

export const LOCALE_LABEL: Record<Locale, string> = {
  en: "English", es: "Español", de: "Deutsch", fr: "Français",
  nl: "Nederlands", ar: "العربية", ja: "日本語",
};

// The only RTL script in the shipped set. A list rather than a boolean so
// adding Hebrew/Farsi/Urdu is a one-line change.
const RTL: Locale[] = ["ar"];

const STORAGE_KEY = "radha.locale";

let _locale: Locale = "en";
// The full BCP-47 tag (en-IN, ar-AE) — distinct from the pack language. Money
// and dates format against this; only the message pack keys off the language.
let _tag = "en-IN";
// Whether dir/lang have been pushed to <html> at least once this page load.
let _applied = false;

/** Language subtag of a BCP-47 tag, if a pack exists for it. */
function packFor(tag: string | null | undefined): Locale | null {
  const lang = (tag || "").split("-")[0].toLowerCase();
  return (AVAILABLE_LOCALES as string[]).includes(lang) ? (lang as Locale) : null;
}

export function isRtl(l: Locale = _locale): boolean {
  return RTL.includes(l);
}

/** Push dir/lang onto <html> so CSS logical properties mirror the layout and
 *  assistive tech announces the right language. */
function applyDocument(): void {
  if (typeof document === "undefined") return;
  const rtl = isRtl();
  document.documentElement.setAttribute("lang", _tag);
  document.documentElement.setAttribute("dir", rtl ? "rtl" : "ltr");
}

/**
 * Resolve the active locale. Tenant locale is the default; a stored personal
 * choice wins. Safe to call on every shell render — a no-op when unchanged.
 */
export function resolveLocale(tenantTag: string | null | undefined): void {
  let stored: string | null = null;
  try { stored = localStorage.getItem(STORAGE_KEY); } catch { /* private mode */ }
  const nextTag = stored || tenantTag || "en-IN";
  const nextPack = packFor(nextTag) ?? "en";
  // `_applied` matters on the very first call: the defaults above already equal
  // en-IN, so a pure value comparison would short-circuit and <html> would keep
  // whatever dir/lang index.html shipped with — which is how dir stayed unset.
  if (_applied && nextTag === _tag && nextPack === _locale) return;
  _tag = nextTag;
  _locale = nextPack;
  _applied = true;
  applyDocument();
}

/** Personal override. Persisted to this browser only, never to the tenant. */
export function setLocale(l: Locale, regionTag?: string): void {
  if (!PACKS[l]) return;
  _locale = l;
  _tag = regionTag || l;
  try { localStorage.setItem(STORAGE_KEY, _tag); } catch { /* private mode */ }
  applyDocument();
}

/** Drop the personal override and fall back to the tenant's locale. */
export function clearLocaleOverride(tenantTag: string | null | undefined): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* private mode */ }
  _tag = tenantTag || "en-IN";
  _locale = packFor(_tag) ?? "en";
  applyDocument();
}

export function getLocale(): Locale { return _locale; }
/** Active BCP-47 tag, or the tag for an explicitly requested language. */
export function localeTag(l?: Locale): string { return l ? l : _tag; }
export function hasOverride(): boolean {
  try { return !!localStorage.getItem(STORAGE_KEY); } catch { return false; }
}

/** Translate a key with optional {var} interpolation.
 *  Falls back active pack -> English -> the key itself, so a missing
 *  translation degrades to readable English rather than a blank or a crash. */
export function t(key: string, vars?: Record<string, string | number>): string {
  const pack = PACKS[_locale] || PACKS.en;
  let s = pack[key] ?? PACKS.en[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
    }
  }
  return s;
}
